import React, { useEffect, useState, useMemo } from "react";
import { Col, Divider, Form, Row } from "antd";
import { useEffectOnce } from "react-use";
import Text from "../Text/Text";
import Button from "../Button/Button";
import Select from "../Select/Select";
import { defaultPropsFormRequire } from "../../utility/DefaultProps";
import { zoneDatasource } from "../../datasource/ZoneDatasource";
import { CustomModal, ZoneWrapper } from "../../pages/SettingOverCreditPage/StyleContent";
import color from "../../resource/color";
import { InfoCircleOutlined } from "@ant-design/icons";
import type { OverCreditApprovedEntities } from "../../entities/OverCreditApproveEntity";

interface OptionType {
  label: string;
  value: string;
  key: string;
}

interface Props {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: any) => void;
  callBackData: any;
  groupData: OverCreditApprovedEntities | null;
}

const ModalApproveGroup: React.FC<Props> = ({
  open,
  onClose,
  onSubmit,
  callBackData,
  groupData,
}) => {
  const [zone, setZone] = useState<OptionType[]>([]);
  const [team, setTeam] = useState<OptionType[]>([]);
  const [form] = Form.useForm();
  const userProfile = JSON.parse(localStorage.getItem("profile")!);
  const [isFormValid, setIsFormValid] = useState(false);
  const [teamId, setTeamId] = useState<string>("");
  const [teamName, setTeamName] = useState<string>("");

  const fetchZonesForTeam = async (teamId: string) => {
    try {
      const zoneRes = await zoneDatasource.getAllZoneByTeam(teamId);
      const zoneData = zoneRes?.zone.map((item: any) => ({
        label: item.zoneName,
        value: item.zoneName,
        key: item.zoneId,
      }));
      setZone(zoneData);
    } catch (error) {
      console.error("Error fetching zones for team:", error);
    }
  };

  const fetchTeamData = async () => {
    try {
      const teamRes = await zoneDatasource.getAllTeam({
        page: 0,
        take: 0,
        company: userProfile?.company,
        status: "ACTIVE",
      });
      const teamData = teamRes.data.map((item: any) => ({
        label: item.teamName,
        value: item.teamId,
        key: item.teamId,
      }));
      setTeam(teamData);
    } catch (error) {
      console.error("Error fetching teams:", error);
    }
  };
  useEffectOnce(() => {
    fetchTeamData();
  });

  useEffect(() => {
    if (open) {
      if (groupData?.teamId) {
        form.setFieldsValue({
          team: groupData.teamId,
          zone: typeof groupData.zones === "string" ? groupData.zones.split(",") : groupData.zones,
        });
        setTeamName(groupData.teamName);
        setTeamId(groupData.teamId);
      } else {
        form.resetFields();
        setTeamName("");
        setTeamId("");
      }
    }
  }, [groupData, open, form]);

  useEffect(() => {
    if (teamId) {
      fetchZonesForTeam(teamId);
    } else {
      setZone([]);
    }
  }, [teamId]);

  const handleFinish = (values: any) => {
    const data: OverCreditApprovedEntities = {
      overCreditApproverGroupId: groupData?.overCreditApproverGroupId || `temp-${Date.now()}`,
      company: userProfile?.company,
      teamId,
      teamName,
      zones: Array.isArray(values.zone) ? values.zone.join(",") : "",
      createBy: `${userProfile?.firstname} ${userProfile?.lastname}`,
      updateBy: `${userProfile?.firstname} ${userProfile?.lastname}`,
      createDate: new Date().toISOString(),
      updateDate: new Date().toISOString(),
      isDelete: false,
      sequences: groupData?.sequences ?? [],
    };
    onSubmit(data);
    form.resetFields();
  };

  const handleClose = () => {
    form.resetFields();
    onClose();
  };

  const checkFormValid = () => {
    const hasErrors = form.getFieldsError().some(({ errors }) => errors.length);
    const hasAllRequired = form.getFieldsValue(["team", "zone"]);
    const isComplete =
      !!hasAllRequired.team && Array.isArray(hasAllRequired.zone) && hasAllRequired.zone.length > 0;
    setIsFormValid(!hasErrors && isComplete);
  };

  const Footer = useMemo(
    () => (
      <Row justify='end' style={{ padding: "0 18px" }}>
        <Col span={6} style={{ paddingRight: 5 }}>
          <Button
            typeButton='primary-light'
            title='ยกเลิก'
            style={{ width: "100%" }}
            onClick={handleClose}
          />
        </Col>
        <Col span={6} style={{ paddingLeft: 5 }}>
          <Button
            typeButton={!isFormValid ? "disabled" : "primary"}
            title='บันทึก'
            style={{ width: "100%" }}
            htmlType='submit'
            onClick={() => form.submit()}
            disabled={!isFormValid}
          />
        </Col>
      </Row>
    ),
    [form, isFormValid],
  );

  return (
    <CustomModal maskClosable={false} open={open} footer={Footer} onCancel={handleClose}>
      <div style={{ padding: "0 18px" }}>
        <Text fontSize={20} fontWeight={700}>
          {groupData?.teamId ? "แก้ไขเกณฑ์อนุมัติ" : "เพิ่มเกณฑ์อนุมัติ"}
        </Text>
      </div>
      <Divider />
      <div style={{ padding: "0 18px" }}>
        <Form
          onFieldsChange={checkFormValid}
          {...defaultPropsFormRequire}
          form={form}
          onFinish={handleFinish}
          style={{ marginTop: 26 }}
          onValuesChange={(changedValues) => {
            if ("team" in changedValues) {
              form.setFieldsValue({ zone: [] });
            }
            checkFormValid();
          }}
        >
          <Form.Item label='ทีม' name='team' rules={[{ required: true, message: "กรุณาเลือกทีม" }]}>
            <Select
              data={team}
              placeholder='ระบุทีม'
              style={{ marginBottom: 8 }}
              onChange={(value) => {
                const selectedTeam = team.find((item) => item.value === value);
                setTeamName(selectedTeam?.label || "");
                setTeamId(selectedTeam?.key || "");
              }}
            />
          </Form.Item>
          {teamId && (
            <ZoneWrapper show={!!teamId}>
              <Form.Item
                label='เขต'
                name='zone'
                rules={[
                  { required: true, message: "กรุณาเลือกเขต" },
                  {
                    validator: async (_, value: string[]) => {
                      if (!Array.isArray(value) || value.length === 0) {
                        return Promise.resolve();
                      }

                      const selectedZones = value.filter((zone) => zone?.trim() !== "");

                      const usedZones = callBackData
                        ?.filter(
                          (entry) =>
                            entry?.overCreditApproverGroupId !==
                            groupData?.overCreditApproverGroupId,
                        )
                        ?.map((entry) => {
                          if (Array.isArray(entry.zones)) return entry.zones;
                          if (typeof entry.zones === "string") return entry.zones.split(",");
                          return [];
                        })
                        .flat()
                        .filter((zone) => zone?.trim() !== "");

                      const hasDuplicate = selectedZones.some((zone) => usedZones.includes(zone));

                      if (hasDuplicate) {
                        return Promise.reject(
                          new Error(
                            "เขตที่เลือกบางเขตถูกใช้งานแล้ว กรุณาเลือกเขตใหม่ที่ยังไม่ถูกใช้งาน",
                          ),
                        );
                      }

                      return Promise.resolve();
                    },
                  },
                ]}
              >
                <Select data={zone} placeholder='ระบุ / เลือกเขต' mode='multiple' />
              </Form.Item>
            </ZoneWrapper>
          )}
        </Form>
      </div>

      <Divider />
    </CustomModal>
  );
};

export default ModalApproveGroup;
